// Intake validation. Deliberately free of pdfjs so it can be exercised
// anywhere, including outside a browser.
//
// Three refusals, each with its own message: wrong type, too large, and no text
// layer. The last one is the one that matters most in practice — a great many
// federal plans in circulation are scans, and telling someone "0 findings"
// when the truth is "this document is a photograph" would be the worst possible
// failure for a tool whose whole claim is traceability.

import {
  MAX_FILE_BYTES,
  MIN_TEXT_CHARS,
  type IngestRejection,
  type IngestWarning,
} from '../../types';

/**
 * "61.4 MB", and "50 MB" rather than "50.0 MB" — a limit stated in whole
 * megabytes should read as one.
 */
function formatMb(bytes: number): string {
  const mb = (bytes / (1024 * 1024)).toFixed(1);
  return `${mb.endsWith('.0') ? mb.slice(0, -2) : mb} MB`;
}

export function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

/** Name and size are all that is needed; the bytes are not read. */
interface FileFacts {
  name: string;
  type: string;
  size: number;
}

/**
 * What the reviewer picked, in the words they would use for it.
 *
 * The extension is what they see in the file dialog, so it leads. The MIME type
 * is the fallback for a file with none — "application/vnd.openxmlformats-
 * officedocument.spreadsheetml.sheet" is accurate and useless, but "an Excel
 * spreadsheet" is not.
 */
function fileKindPhrase(type: string, name: string): string {
  const extension = /\.([a-z0-9]{1,6})$/i.exec(name)?.[1]?.toLowerCase();
  if (extension) return `a .${extension} file`;

  const byMime: Record<string, string> = {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      'an Excel spreadsheet',
    'application/vnd.ms-excel': 'an Excel spreadsheet',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      'a Word document',
    'application/msword': 'a Word document',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      'a PowerPoint file',
    'text/csv': 'a CSV file',
    'text/plain': 'a plain text file',
    'application/zip': 'a zip archive',
    'application/x-zip-compressed': 'a zip archive',
    'message/rfc822': 'an email',
  };
  if (byMime[type]) return byMime[type];
  if (type.startsWith('image/')) return `an image (${type.slice(6).toUpperCase()})`;
  return 'a file of a type Ceiling cannot read';
}

export function validateFile(file: FileFacts): IngestRejection | null {
  const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

  if (!isPdf) {
    return {
      reason: 'not-pdf',
      message: `That’s ${fileKindPhrase(file.type, file.name)}. Ceiling reads PDFs.`,
      detail: `Ceiling works from the text layer of a PDF, so it needs the plan exported as one — File, then Save as PDF or Print to PDF, in whatever produced ${file.name}. Then drop the PDF here.`,
    };
  }

  if (file.size > MAX_FILE_BYTES) {
    return {
      reason: 'too-large',
      message: `That file is ${formatMb(file.size)}. The limit is ${formatMb(MAX_FILE_BYTES)}.`,
      detail:
        'The document is read in your browser, so a file this large would exhaust the tab. Removing image appendices or splitting the document will usually bring it under the limit.',
    };
  }

  return null;
}

/** Counts non-whitespace characters, the measure MIN_TEXT_CHARS applies to. */
export function textDensity(rawText: string): number {
  return rawText.replace(/\s/g, '').length;
}

export function validateTextLayer(
  rawText: string,
  pageCount: number,
): IngestRejection | null {
  const chars = textDensity(rawText);
  if (chars >= MIN_TEXT_CHARS) return null;

  return {
    reason: 'no-text-layer',
    message:
      'This PDF looks like a scan — there’s no selectable text in it, so Ceiling can’t read the clauses. If you have a text-based version, try that.',
    detail: `Only ${chars} characters came out of ${plural(pageCount, 'page')}, which means the pages are pictures of a document rather than a document. Ceiling reads the text layer and does not perform OCR. A PDF exported from the original file, rather than scanned from paper, will work.`,
  };
}

/* ==========================================================================
   Warnings — things worth saying about a document that did ingest
   ========================================================================== */

/** Enough text to work with, but thinner than a plan of this length should be. */
const THIN_TEXT_PER_PAGE = 400;

export interface ParseSummary {
  pageCount: number;
  textChars: number;
  /** True when section detection fell back to one unnumbered "Document". */
  sectionsUnstructured: boolean;
  sectionCount: number;
  tableCount: number;
}

export function warningsFor(summary: ParseSummary): IngestWarning[] {
  const warnings: IngestWarning[] = [];

  if (summary.sectionsUnstructured) {
    warnings.push({
      reason: 'no-sections',
      message: 'No numbered sections were found in this document.',
      detail:
        'Section detection looks for numbered headings — "3.2.1 Fixture Power" and the like. This document has none that could be read, so it is treated as one continuous section. The checks still run: they read the whole text, and citations, estimated parameters and tables are all found without section numbers. What changes is that a finding will be located by page rather than by section, and the per-ECM completeness check cannot run because there are no ECM sections to check.',
      action:
        'Worth a glance at the page count and table list below to confirm this is the document you meant. An M&V plan with no numbered sections is unusual; a scanned-then-OCRed file or an extract of one chapter would both look like this.',
    });
  }

  if (summary.tableCount === 0) {
    warnings.push({
      reason: 'no-tables',
      message: 'No tables were found in this document.',
      detail:
        'Table reconciliation needs a savings table with one row per ECM and a total. Nothing in this document has that shape, so that check will not run and will be reported as skipped.',
      action:
        'The other checks are unaffected. If the plan does contain savings tables, they may be images rather than text, in which case no tool reading the text layer will find them.',
    });
  }

  if (
    summary.pageCount > 0 &&
    summary.textChars / summary.pageCount < THIN_TEXT_PER_PAGE
  ) {
    warnings.push({
      reason: 'thin-text',
      message: `This document averages ${Math.round(summary.textChars / summary.pageCount)} characters of text per page.`,
      detail:
        'That is thin for an engineering document. It usually means most pages are images with a little text on top — a scan with a text overlay, or a plan whose tables and figures were pasted in as pictures.',
      action:
        'Read the section and table lists below against the real document. If they look sparse beside it, the text layer is incomplete and the findings will be incomplete with it.',
    });
  }

  return warnings;
}

/* ==========================================================================
   Turning an exception into something a user can act on
   ========================================================================== */

/**
 * pdf.js throws typed exceptions. Each one gets written copy; the raw message
 * is never shown, because "Invalid PDF structure." tells a reviewer nothing
 * about what to do next. The caller logs the original to the console.
 */
export function rejectionForReadError(error: unknown): IngestRejection {
  const name = error instanceof Error ? error.name : '';

  if (name === 'PasswordException') {
    return {
      reason: 'encrypted',
      message:
        'This file couldn’t be opened (it’s password-protected). Nothing left your browser.',
      detail:
        'Ceiling does not prompt for a password: it reads documents in your browser and holds nothing, so there is nowhere safe for it to put one. Open the file in a PDF reader with the password, save an unprotected copy, and drop that here instead.',
    };
  }

  if (name === 'InvalidPDFException') {
    return {
      reason: 'corrupt',
      message:
        'This file couldn’t be opened (its internal structure couldn’t be read). Nothing left your browser.',
      detail:
        'It begins like a PDF but its internals could not be parsed, which usually means the download was truncated or the file was altered in transit. Downloading it again, or re-exporting it from whatever produced it, normally fixes this.',
    };
  }

  if (name === 'MissingPDFException' || name === 'UnexpectedResponseException') {
    return {
      reason: 'unreadable',
      message:
        'This file couldn’t be opened (it could not be read to the end). Nothing left your browser.',
      detail:
        'If it came from a network drive or a slow share, copying it to this machine first and selecting the local copy usually works.',
    };
  }

  return {
    reason: 'unreadable',
    message:
      'This file couldn’t be opened (the PDF reader could not make sense of it). Nothing left your browser.',
    detail:
      'If it opens correctly in a PDF reader, re-exporting or re-saving it from there will usually produce a file this can read. The technical detail has been written to the browser console.',
  };
}
